import { useTranslation } from "react-i18next";
import { useDisableCRUD } from "../../../../store/admin/companiesStore";
import { useIsMonitor } from "../../../../hooks/useIsMonitor";
import { CRUDTooltip } from "../../../../components/base/CRUDTooltip";
import type { User, ThemeClasses } from "../types";

interface UserListRowProps {
  user: User;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onDetails: (user: User) => void;
  onEdit: (user: User) => void;
  onDelete?: (user: User) => void;
  onHardDelete?: (user: User) => void;
  onActivate?: (user: User) => void;
  onChangePassword?: (user: User) => void;
  isAdminReseller?: boolean;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelection?: (user: User) => void;
}

export function UserListRow({
  user,
  themeClasses,
  isDarkMode,
  onDetails,
  onEdit,
  onDelete,
  onHardDelete,
  onActivate,
  onChangePassword,
  isAdminReseller,
  isSelectionMode = false,
  isSelected = false,
  onToggleSelection,
}: UserListRowProps) {
  const { t } = useTranslation("adminSections");
  const disableCRUD = useDisableCRUD();
  const { isMonitor, isReadOnly } = useIsMonitor();
  const getStatusColor = (status: string): string => {
    switch (status) {
      case "active":
        return "text-primary-500 bg-primary-500/10";
      case "inactive":
        return "text-red-500 bg-red-500/10";
      default:
        return "text-gray-500 bg-gray-500/10";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case "active":
        return t("users.status.active");
      case "inactive":
        return t("users.status.inactive");
      default:
        return status || t("users.status.unknown");
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
  };

  const handleRowClick = () => {
    if (isSelectionMode && onToggleSelection) {
      onToggleSelection(user);
    }
  };

  return (
    <div
      className={`${themeClasses.bgInput} border ${themeClasses.cardBorder} rounded-xl p-4 hover:shadow-md transition-all flex items-center justify-between gap-2 ${
        isSelectionMode ? "cursor-pointer" : ""
      } ${isSelected ? "ring-2 ring-primary-500" : ""}`}
      onClick={isSelectionMode ? handleRowClick : undefined}
    >
      {/* Selection Checkbox */}
      {isSelectionMode && (
        <div className="flex items-center mr-2">
          <div
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
              isSelected
                ? "bg-primary-500 border-primary-500"
                : isDarkMode
                  ? "border-gray-500"
                  : "border-gray-300"
            }`}
          >
            {isSelected && <i className="ri-check-line text-white text-sm"></i>}
          </div>
        </div>
      )}

      {/* Left - User Info */}
      <div className="flex items-center gap-2 justify-center min-w-0">
        {/* Status - al lado izquierdo */}
        <div
          className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isDarkMode ? "bg-dark-border" : "bg-gray-100"
          }`}
        >
          <span className={`text-sm font-semibold ${themeClasses.text}`}>
            {getInitials(user.first_name, user.last_name)}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <h3 className={`font-medium ${themeClasses.text} truncate`}>
            {user.first_name} {user.last_name}
          </h3>
          <div className="flex gap-2 items-center">
            <div className="hidden sm:block">
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                  user.status_id,
                )}`}
              >
                {getStatusLabel(user.status_id)}
              </span>
            </div>
            <p className={`text-sm ${themeClasses.textSecondary} truncate`}>
              @{user.username || t("users.fields.noUsername")}
            </p>
          </div>
        </div>
      </div>

      {/* Center - Company & Role Info */}
      {/* <div className="hidden md:flex items-center space-x-6 flex-1">
        {user.company && (
          <div className="flex items-center space-x-2 min-w-0">
            <i className={`ri-building-line ${themeClasses.textSecondary}`}></i>
            <span className={`text-sm ${themeClasses.textSecondary} truncate`}>
              {user.company.name}
            </span>
          </div>
        )}
        {user.role && (
          <div className="flex items-center space-x-2">
            <i
              className={`ri-shield-user-line ${themeClasses.textSecondary}`}
            ></i>
            <span
              className={`text-sm ${themeClasses.textSecondary} capitalize`}
            >
              {user.role.name}
            </span>
          </div>
        )}
      </div> */}

      {/* Right - Actions */}
      <div className="flex items-center space-x-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDetails(user);
          }}
          className="py-1.5 px-3 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg text-xs font-medium transition-colors flex items-center gap-1"
          title={t("users.actions.details")}
        >
          <i className="ri-eye-line text-sm"></i>
          <span className="hidden lg:inline">{t("users.actions.details")}</span>
        </button>
        {!isMonitor && !isReadOnly && (
          <CRUDTooltip>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(user);
              }}
              disabled={disableCRUD}
              className={`py-1.5 px-3 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} ${themeClasses.buttonTextHover} text-xs font-medium transition-colors flex items-center gap-1`}
              title={t("users.actions.edit")}
            >
              <i className="ri-pencil-line text-sm"></i>
              <span className="hidden lg:inline">
                {t("users.actions.edit")}
              </span>
            </button>
          </CRUDTooltip>
        )}
        {/* Desactivar - solo para usuarios activos y admin_reseller */}
        {user.status_id === "R5tW0uX4yY9cF6dH2aI8" &&
          isAdminReseller &&
          onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(user);
              }}
              disabled={isSelectionMode || disableCRUD}
              className={`py-1.5 px-2 bg-orange-50 hover:bg-orange-100 text-orange-600 rounded-lg text-xs font-medium transition-colors flex items-center gap-0.5 ${isSelectionMode || disableCRUD ? "opacity-50 cursor-not-allowed" : ""}`}
              title={t("users.actions.deactivate")}
            >
              <i className="ri-close-circle-line text-sm"></i>
              <span className="hidden lg:inline">
                {t("users.actions.deactivateShort")}
              </span>
            </button>
          )}
        {/* Activar - solo para usuarios inactivos y admin_reseller */}
        {user.status_id === "S6uX1vY5zZ0dG7eI3bJ9" &&
          isAdminReseller &&
          onActivate && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onActivate(user);
              }}
              disabled={isSelectionMode || disableCRUD}
              className={`py-1.5 px-2 bg-green-50 hover:bg-green-100 text-green-600 rounded-lg text-xs font-medium transition-colors flex items-center gap-0.5 ${isSelectionMode || disableCRUD ? "opacity-50 cursor-not-allowed" : ""}`}
              title={t("users.actions.activate")}
            >
              <i className="ri-check-line text-sm"></i>
              <span className="hidden lg:inline">
                {t("users.actions.activateShort")}
              </span>
            </button>
          )}
        {/* Eliminar - solo para usuarios inactivos y admin_reseller */}
        {user.status_id === "S6uX1vY5zZ0dG7eI3bJ9" &&
          isAdminReseller &&
          onHardDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onHardDelete(user);
              }}
              disabled={isSelectionMode || disableCRUD}
              className={`py-1.5 px-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-xs font-medium transition-colors flex items-center gap-0.5 ${isSelectionMode || disableCRUD ? "opacity-50 cursor-not-allowed" : ""}`}
              title={t("users.actions.deletePermanently")}
            >
              <i className="ri-delete-bin-line text-sm"></i>
              <span className="hidden lg:inline">
                {t("users.actions.delete")}
              </span>
            </button>
          )}
        {/* Cambiar contraseña - solo para admin_reseller */}
        {isAdminReseller && onChangePassword && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onChangePassword(user);
            }}
            disabled={isSelectionMode}
            className={`py-1.5 px-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg text-xs font-medium transition-colors flex items-center gap-0.5 ${isSelectionMode || disableCRUD ? "opacity-50 cursor-not-allowed" : ""}`}
            title={t("users.actions.changePassword")}
          >
            <i className="ri-lock-line text-sm"></i>
            <span className="hidden lg:inline">
              {t("users.actions.changePasswordShort")}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
